import { useState, useRef, useEffect, useLayoutEffect } from 'react';

const TOOLTIP_MAX_WIDTH = 260;
const VIEWPORT_MARGIN = 8;

// Tap-to-reveal tooltip for text truncated with CSS ellipsis. Touch devices never
// fire the native `title` tooltip, so this gives mobile a way to read the full
// value — same interaction pattern as KpiTooltip's isMobile mode, but wrapping the
// truncated text itself rather than a separate info icon.
const TruncatedTextTooltip = ({ text, children }: { text: string; children: React.ReactNode }) => {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0, caretLeft: 0 });
    const triggerRef = useRef<HTMLSpanElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    const measure = () => {
        if (!triggerRef.current) return;
        const r = triggerRef.current.getBoundingClientRect();
        const centerX = r.left + r.width / 2;
        const idealLeft = centerX - TOOLTIP_MAX_WIDTH / 2;
        const clampedLeft = Math.max(VIEWPORT_MARGIN, Math.min(idealLeft, window.innerWidth - TOOLTIP_MAX_WIDTH - VIEWPORT_MARGIN));
        setPos({ top: r.top - 8, left: clampedLeft, caretLeft: centerX - clampedLeft });
    };

    // After the tooltip renders, reposition using its actual width so short text
    // doesn't leave unused space and the caret stays centred on the trigger.
    useLayoutEffect(() => {
        if (!open || !tooltipRef.current || !triggerRef.current) return;
        const actualWidth = tooltipRef.current.getBoundingClientRect().width;
        const r = triggerRef.current.getBoundingClientRect();
        const centerX = r.left + r.width / 2;
        const idealLeft = centerX - actualWidth / 2;
        const clampedLeft = Math.max(VIEWPORT_MARGIN, Math.min(idealLeft, window.innerWidth - actualWidth - VIEWPORT_MARGIN));
        setPos({ top: r.top - 8, left: clampedLeft, caretLeft: centerX - clampedLeft });
    }, [open]);

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!open) measure();
        setOpen((v) => !v);
    };

    useEffect(() => {
        if (!open) return;
        const close = () => setOpen(false);
        window.addEventListener('scroll', close, { passive: true, capture: true });
        return () => window.removeEventListener('scroll', close, { capture: true });
    }, [open]);

    // Dismiss on a tap anywhere outside the trigger.
    useEffect(() => {
        if (!open) return;
        const onPointerDown = (e: PointerEvent) => {
            if (triggerRef.current?.contains(e.target as Node)) return;
            setOpen(false);
        };
        document.addEventListener('pointerdown', onPointerDown, true);
        return () => document.removeEventListener('pointerdown', onPointerDown, true);
    }, [open]);

    return (
        <span ref={triggerRef} onClick={handleClick} style={{ cursor: 'pointer', display: 'inline-block', minWidth: 0, maxWidth: '100%' }}>
            {children}
            {open && (
                <div
                    ref={tooltipRef}
                    style={{
                        position: 'fixed',
                        top: pos.top,
                        left: pos.left,
                        transform: 'translateY(-100%)',
                        width: 'max-content',
                        maxWidth: TOOLTIP_MAX_WIDTH,
                        background: 'var(--surface, #fff)',
                        border: '1px solid var(--card-border, #E2E8F0)',
                        borderRadius: 8,
                        padding: '8px 10px',
                        fontSize: 11,
                        color: 'var(--t1, #1A2233)',
                        fontWeight: 500,
                        lineHeight: 1.5,
                        whiteSpace: 'normal',
                        textAlign: 'left',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.14)',
                        zIndex: 9999,
                        pointerEvents: 'none',
                    }}
                >
                    {text}
                    <div style={{
                        position: 'absolute',
                        bottom: -5,
                        left: pos.caretLeft,
                        transform: 'translateX(-50%) rotate(45deg)',
                        width: 8,
                        height: 8,
                        background: 'var(--surface, #fff)',
                        borderRight: '1px solid var(--card-border, #E2E8F0)',
                        borderBottom: '1px solid var(--card-border, #E2E8F0)',
                    }} />
                </div>
            )}
        </span>
    );
};

export default TruncatedTextTooltip;
